import React, { useEffect, useState } from "react";
import { Calendar, CheckCircle2, XCircle, Loader2, AlertTriangle, ExternalLink, LinkIcon, Copy, Info } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function CRMSettings() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/crm/google/config-status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ configured: false, connected: false }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // If we just returned from OAuth (?google=connected in URL), refresh once
    if (window.location.search.includes("google=connected")) {
      toast.success("Google Calendar collegato con successo");
      const url = new URL(window.location.href);
      url.searchParams.delete("google");
      window.history.replaceState({}, "", url.pathname);
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.get("/crm/google/authorize");
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile avviare il collegamento");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Scollegare Google Calendar? Non potrai più creare eventi automaticamente.")) return;
    setBusy(true);
    try {
      await api.post("/crm/google/disconnect");
      toast.success("Google Calendar scollegato");
      load();
    } catch { toast.error("Errore"); }
    finally { setBusy(false); }
  };

  const copyRedirect = async () => {
    if (!status?.redirect_uri) return;
    try {
      await navigator.clipboard.writeText(status.redirect_uri);
      toast.success("Redirect URI copiato");
    } catch { toast.error("Impossibile copiare"); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-4xl" data-testid="crm-settings-page">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-widest text-[#FF5A1F]">— CRM</div>
        <h1 className="font-display text-5xl uppercase text-[#0A0A0A] mt-2">Impostazioni</h1>
        <p className="text-[#0A0A0A]/60 mt-2 max-w-2xl">Integrazioni personali e configurazione dell'account.</p>
      </div>

      {/* Google Calendar Card */}
      <section className="border-2 border-[#0A0A0A] bg-[#FAFAF7] p-6" data-testid="google-calendar-card">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-11 h-11 border-2 border-[#0A0A0A] flex items-center justify-center bg-white">
              <Calendar size={22} className="text-[#FF5A1F]" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="font-display text-2xl uppercase text-[#0A0A0A]">Google Calendar</h2>
              <p className="text-sm text-[#0A0A0A]/60 mt-1">Crea automaticamente eventi Google Meet quando fissi appuntamenti con i lead.</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-[#0A0A0A]/50 py-4">
            <Loader2 size={16} className="animate-spin" /> Caricamento stato…
          </div>
        ) : !status?.configured ? (
          <NotConfigured user={user} />
        ) : status?.connected ? (
          <div className="mt-4 border-2 border-emerald-600/40 bg-emerald-50 p-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 size={20} className="text-emerald-700 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-bold text-emerald-900" data-testid="google-connected-label">Collegato</div>
                <div className="text-sm text-emerald-800 mt-0.5" data-testid="google-connected-email">
                  Account: <strong>{status.connected_email}</strong>
                </div>
                <div className="text-xs text-emerald-800/80 mt-2">
                  Ora quando cambi lo stato di un lead in "Appuntamento" puoi creare l'evento nel tuo calendario con un clic.
                </div>
              </div>
              <button onClick={disconnect} disabled={busy} className="btn-outline-dark text-xs py-2 px-4 shrink-0" data-testid="google-disconnect-btn">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />} Scollega
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-4">
            <div className="mb-4 border-2 border-[#FF5A1F]/30 bg-[#FF5A1F]/5 p-4">
              <div className="flex items-start gap-3">
                <Info size={18} className="text-[#FF5A1F] shrink-0 mt-0.5" />
                <div className="text-sm text-[#0A0A0A]/80">
                  Clicca "Collega Google Calendar" → si apre la pagina di Google → accedi con il tuo account (deve essere il tuo account di lavoro Perla Music) → conferma i permessi. Verrai riportato qui.
                </div>
              </div>
            </div>
            <button onClick={connect} disabled={busy} className="btn-cta" data-testid="google-connect-btn">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <LinkIcon size={14} />} Collega Google Calendar
            </button>
          </div>
        )}
      </section>

      {/* Admin-only: redirect URI helper */}
      {user?.role === "admin" && status?.configured && (
        <section className="mt-6 border-2 border-[#0A0A0A]/20 bg-[#FAFAF7] p-6" data-testid="google-admin-info">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/50 mb-2">Info tecniche (solo admin)</div>
          <div className="text-sm space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-[#0A0A0A]/60">Redirect URI configurato:</span>
              <code className="bg-white border border-[#0A0A0A]/10 px-2 py-1 text-xs font-mono">{status.redirect_uri}</code>
              <button onClick={copyRedirect} className="text-[#0A0A0A]/60 hover:text-[#FF5A1F]"><Copy size={13} /></button>
            </div>
            <p className="text-xs text-[#0A0A0A]/50">Questo URI dev'essere aggiunto tra gli "Authorized redirect URI" in Google Cloud Console → Credentials → OAuth 2.0 Client ID.</p>
          </div>
        </section>
      )}
    </div>
  );
}

const NotConfigured = ({ user }) => (
  <div className="mt-4 border-2 border-amber-500/40 bg-amber-50 p-4">
    <div className="flex items-start gap-3">
      <AlertTriangle size={20} className="text-amber-700 shrink-0 mt-0.5" />
      <div className="text-sm text-amber-900">
        <div className="font-bold" data-testid="google-not-configured">Integrazione non ancora configurata</div>
        <p className="mt-1">
          L'admin deve prima creare un OAuth Client su Google Cloud Console e configurare <code className="bg-white/60 px-1">GOOGLE_CLIENT_ID</code> e <code className="bg-white/60 px-1">GOOGLE_CLIENT_SECRET</code> nel backend.
        </p>
        {user?.role === "admin" && (
          <a href="/admin/credenziali-crm" className="mt-3 inline-flex items-center gap-1 text-[#FF5A1F] font-bold text-xs uppercase tracking-widest hover:underline">
            Vai alla checklist credenziali <ExternalLink size={12} />
          </a>
        )}
      </div>
    </div>
  </div>
);
